import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { GetDisbursementPayoutBatches200, PayoutBatches } from 'api';

import type { ApprovalAction, ApprovalStatus } from 'models/ApprovalWorkFlow';
import { APPROVAL_STATUSES } from 'models/ApprovalWorkFlow';
import { BATCHES_STATUS } from 'models/batches';

import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { BatchesActionButtons } from './BatchesActionButtons';
import { getColumns } from './columns';
import { BATCHES_TABLE_TEST_ID } from './constants';
import { BatchDetails } from './Details/BatchDetails';
import type { FiltersState, LocalState } from './localState';

import type { GlobalFiltersType, RefetchBatchesType } from '.';

interface Props {
  globalFilters: GlobalFiltersType;
  data: GetDisbursementPayoutBatches200 | undefined;
  isLoading: boolean;
  localState: LocalState;
  setFilters: (filters: FiltersState) => void;
  updateCursorAfter: (value: string | null) => void;
  isBatchApprovalRequired: boolean;
  refreshKey?: string | number;
  refetchBatches: RefetchBatchesType;
}

export const BatchesTable = ({
  globalFilters,
  data,
  isLoading,
  localState,
  setFilters,
  updateCursorAfter,
  isBatchApprovalRequired,
  refreshKey,
  refetchBatches
}: Props) => {
  const [searchParams] = useSearchParams();
  const batchesColumns = useMemo(
    () => getColumns(isBatchApprovalRequired),
    [isBatchApprovalRequired]
  );

  useEffect(() => {
    const status = searchParams.get('status') as ApprovalStatus;
    if (!status) return;

    const loadStatusFromSearchParamsQuery =
      isBatchApprovalRequired &&
      status &&
      localState.filters.status === undefined &&
      APPROVAL_STATUSES.includes(status);

    if (loadStatusFromSearchParamsQuery) {
      setFilters({ status: status });
      updateCursorAfter(null);
    }
  }, [
    isBatchApprovalRequired,
    localState.filters.status,
    searchParams,
    setFilters,
    updateCursorAfter
  ]);

  useEffect(() => {
    if (isBatchApprovalRequired && localState.filters.status === undefined)
      setFilters({ status: 'waiting_review' });
  }, [isBatchApprovalRequired, localState.filters.status, setFilters]);

  const [selectedRowData, setSelectedRowData] = useState<PayoutBatches>();

  const handleRowClick = (record: PayoutBatches) => {
    setSelectedRowData((selected) =>
      selected !== record ? record : undefined
    );
  };

  const {
    onScroll,
    data: batchesData,
    optimisticallyUpdateElement
  } = useVirtualList<PayoutBatches>({
    data: data?.data,
    uniqueKey: 'id',
    isFetching: isLoading,
    initialRefValue: data?.meta?.cursor_next,
    updateCursorCallback: updateCursorAfter,
    resetConditions: [
      globalFilters,
      localState.filters,
      updateCursorAfter,
      refreshKey
    ],
    cursorNext: data?.meta?.has_next_page ? data?.meta?.cursor_next : undefined
  });

  return (
    <DataTable
      total={data?.meta?.total}
      tableActions={<BatchesActionButtons refetchBatches={refetchBatches} />}
      dataSource={batchesData}
      columns={batchesColumns}
      onScroll={onScroll}
      onRowClick={handleRowClick}
      isLoading={isLoading}
      rowKey="id"
      selectedRow={selectedRowData}
      tableDataTestId={BATCHES_TABLE_TEST_ID}
      rowDataTestId="batches-table-row-entry"
      rightPanel={
        selectedRowData ? (
          <BatchDetails
            onClose={() => setSelectedRowData(undefined)}
            selectedRowData={selectedRowData}
            onApproveOrReject={(action: ApprovalAction) =>
              optimisticallyUpdateElement({
                ...selectedRowData,
                status:
                  action === 'approve'
                    ? BATCHES_STATUS.COMPLETED
                    : BATCHES_STATUS.REJECTED
              })
            }
          />
        ) : undefined
      }
    />
  );
};
